import * as pdfLib from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import * as FileSystem from 'expo-file-system';
import { Platform } from 'react-native';
import { Ref } from 'react';
import ViewShot from 'react-native-view-shot';
import { DateRange } from '../../redux/dateRangeSlice';

// eslint-disable-next-line no-shadow
export enum PageSize {
  // eslint-disable-next-line no-unused-vars
  A4 = 'A4', LETTER = 'Letter',
}

const pageSizeValues: { [key: string]: [number, number] } = {
  [PageSize.A4]: pdfLib.PageSizes.A4,
  [PageSize.LETTER]: pdfLib.PageSizes.Letter,
};

// eslint-disable-next-line no-shadow
export enum PageOrientation {
  // eslint-disable-next-line no-unused-vars
  PORTRAIT = 'Portrait', LANDSCAPE = 'Landscape'
}

export type ImagePreview = {
  name: string,
  uri: string,
}

// eslint-disable-next-line no-shadow
export enum SolveAnalyticsWidget {
  // eslint-disable-next-line no-unused-vars
  TICKETS_RAISED_BY_TEMPLATE = 'Tickets raised by template',
  // eslint-disable-next-line no-unused-vars
  OVERALL_TICKETS_RAISED = 'Overall tickets raised',
  // eslint-disable-next-line no-unused-vars
  PRIORITY_STATISTICS = 'Priority statistics',
  // eslint-disable-next-line no-unused-vars
  TICKETS_RAISED_PER_ASSET = 'Tickets raised per asset',
  // eslint-disable-next-line no-unused-vars
  TIME_ON_TASK = 'Time on task',
  // eslint-disable-next-line no-unused-vars
  TICKET_DETAILS = 'Ticket details',
  // eslint-disable-next-line no-unused-vars
  SLA_TARGETS = 'SLA targets',
  // eslint-disable-next-line no-unused-vars
  SLA_VIOLATIONS_BY_ASSET = 'SLA violations by asset',
  // eslint-disable-next-line no-unused-vars
  SLA_VIOLATIONS_BY_LOCATION = 'SLA violations by location',
  // eslint-disable-next-line no-unused-vars
  MOST_FREQUENT_TICKETS_RAISED = 'Most frequent tickets raised',
  // eslint-disable-next-line no-unused-vars
  SLA_VIOLATIONS_BY_DATE = 'SLA violations',
}

export interface SolveAnalyticsWidgetProps {
  range: DateRange,
  compareRange: DateRange,
  viewShotRef?: Ref<ViewShot>,
  loading: boolean,
  data,
  comparisonData,
}

// New function to convert base64 image data to Uint8Array for pdf-lib
const base64ToUint8Array = (base64) => {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// Convert image URI to bytes needed for PDF
export const getImageBytes = async (uri: string) => {
  if (Platform.OS === 'web') {
    try {
      // Handle data URLs directly for web
      if (uri.startsWith('data:')) {
        const base64 = uri.split(',')[1];
        return base64ToUint8Array(base64);
      }

      // Fallback to fetch for non-data URLs
      const response = await fetch(uri);
      const blob = await response.blob();
      return new Promise((resolve) => {
        // eslint-disable-next-line no-undef
        const reader = new FileReader();
        reader.onload = () => {
          // Cast or check that result is string before using split
          const resultString = reader.result as string;
          const base64 = resultString.split(',')[1];
          resolve(base64ToUint8Array(base64));
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return null;
    }
  } else {
    // For native platforms, read as base64 from file system
    const base64 = await FileSystem.readAsStringAsync(uri, {
      encoding: FileSystem.EncodingType.Base64,
    });
    return base64ToUint8Array(base64);
  }
};

export const imagesToPdf = async (
  images: ImagePreview[],
  size: PageSize,
  orientation: PageOrientation,
  title: string,
  subtitle?: string,
) => {
  try {
    // Create PDF document
    const pdfDoc: pdfLib.PDFDocument = await pdfLib.PDFDocument.create();

    // Add fonts
    pdfDoc.registerFontkit(fontkit);
    const helveticaFont = await pdfDoc.embedFont(pdfLib.StandardFonts.Helvetica);

    const pageDimensions: [number, number] = [...pageSizeValues[size]];
    if (orientation === PageOrientation.LANDSCAPE) pageDimensions.reverse();
    const titlePage: pdfLib.PDFPage = pdfDoc.addPage(pageDimensions);
    const { width, height } = titlePage.getSize();

    // Add title and date
    titlePage.drawText(title, {
      x: 50,
      y: height - 80,
      size: 24,
      font: helveticaFont,
      color: pdfLib.rgb(0.1, 0.1, 0.1),
    });

    titlePage.drawLine({
      start: { x: 50, y: height - 95 },
      end: { x: width - 50, y: height - 90 },
      thickness: 2,
      color: pdfLib.rgb(0.2, 0.4, 0.8),
    });

    titlePage.drawText(`Generated: ${new Date().toLocaleString()}`, {
      x: 50,
      y: height - 115,
      size: 12,
      font: helveticaFont,
      color: pdfLib.rgb(0.4, 0.4, 0.4),
    });

    let currentY: number = height - 160;

    if (subtitle) {
      titlePage.drawText(subtitle, {
        x: 50,
        y: currentY,
        size: 12,
        font: helveticaFont,
        color: pdfLib.rgb(0.4, 0.4, 0.4),
      });
      currentY -= 100;
    }

    images.forEach(async (image: ImagePreview) => {
      try {
        const imageBytes = await getImageBytes(image.uri);

        // Embed image in PDF
        let pdfImage: pdfLib.PDFImage;
        if (image.uri.toLowerCase().endsWith('.jpg') || image.uri.toLowerCase().endsWith('.jpeg')) {
          pdfImage = await pdfDoc.embedJpg(imageBytes as Uint8Array);
        } else {
          pdfImage = await pdfDoc.embedPng(imageBytes as Uint8Array);
        }

        const imageDimensions: { width: number, height: number } = pdfImage.scale(1);
        const imageAspectRatio: number = imageDimensions.width / imageDimensions.height;
        const imageHeight: number = (width - 100) / imageAspectRatio;

        if (currentY < imageHeight + 60) {
          const newPage = pdfDoc.addPage(pageDimensions);
          currentY = newPage.getSize().height - 100;
        }

        const currentPage = pdfDoc.getPages()[pdfDoc.getPageCount() - 1];

        // Draw widget name
        currentPage.drawText(image.name, {
          x: 50,
          y: currentY,
          size: 12,
          font: helveticaFont,
          color: pdfLib.rgb(0.2, 0.2, 0.2),
        });

        // Draw image
        currentPage.drawImage(pdfImage, {
          x: 50,
          y: currentY - imageHeight - 10,
          width: width - 100,
          height: imageHeight,
        });

        // Update position for next image
        currentY -= (imageHeight + 50); // Height + padding
      } catch (error) { /* empty */ }
    });

    const pdfBytes: Uint8Array = await pdfDoc.save();

    const blob: Blob = new Blob([pdfBytes as any], { type: 'application/pdf' });
    return blob;
  } catch (error) {
    return null;
  }
};
